import { useState, useEffect } from "react"
import { useUser } from "@clerk/clerk-react"
import { spotTeam as firestoreSpotTeam, getUserSpottedTeams, resetTripModeSpots, removeTeamSpot } from "../lib/firestore"
import type { Team } from "../types"

type TrackingMode = 'all-time' | 'trip-mode'

interface SpottingState {
  spottingTeams: Set<string> // Teams currently being spotted
  spottedTeamsCache: Map<string, Team & { spottedAt: Date; mode?: TrackingMode }> // Cached spotted teams by ID
  lastSpottedTeam: string | null // For immediate feedback
}

interface EnhancedSpotButtonState {
  isSpotted: boolean
  isSpotting: boolean
  canSpot: boolean
  buttonText: string
  buttonVariant: 'default' | 'secondary' | 'outline'
  showBadge: boolean
}

export function useUserSpots() {
  const { user } = useUser()
  const [spottedTeams, setSpottedTeams] = useState<(Team & { spottedAt: Date; mode?: TrackingMode })[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Current active tracking mode
  const [trackingMode, setTrackingMode] = useState<TrackingMode>('all-time')
  
  // Enhanced spotting state for immediate feedback
  const [spottingState, setSpottingState] = useState<SpottingState>({
    spottingTeams: new Set(),
    spottedTeamsCache: new Map(),
    lastSpottedTeam: null
  })

  const fetchSpottedTeams = async (mode?: TrackingMode) => {
    if (!user?.id) return
    
    try {
      setLoading(true)
      setError(null)
      
      const spotMode = mode || trackingMode
      console.log('Fetching spotted teams for user:', user.id, 'mode:', spotMode)
      
      const teams = await getUserSpottedTeams(user.id, spotMode)
      console.log('Fetched spotted teams:', teams)
      
      // Add type assertion to ensure compatibility with TrackingMode type
      const typedTeams = teams as (Team & { spottedAt: Date; mode?: TrackingMode })[]
      setSpottedTeams(typedTeams)
      
      // Update cache for quick lookups
      setSpottingState(prev => {
        const newCache = new Map(prev.spottedTeamsCache)
        typedTeams.forEach(team => {
          newCache.set(`${team.id}-${team.mode || spotMode}`, team)
        })
        return { ...prev, spottedTeamsCache: newCache }
      })
    } catch (err) {
      console.error('Error fetching spotted teams:', err)
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  // Helper function to check if a team is spotted in the current mode
  const isTeamSpotted = (teamId: string, mode?: TrackingMode): boolean => {
    const checkMode = mode || trackingMode
    return spottingState.spottedTeamsCache.has(`${teamId}-${checkMode}`) ||
           spottedTeams.some(team => team.id === teamId && (team.mode || 'all-time') === checkMode)
  }

  // Helper function to check if a team is currently being spotted
  const isTeamSpotting = (teamId: string): boolean => {
    return spottingState.spottingTeams.has(teamId)
  }

  // Helper function to get spot button state
  const getSpotButtonState = (teamId: string, mode?: TrackingMode): EnhancedSpotButtonState => {
    const checkMode = mode || trackingMode
    const isSpotted = isTeamSpotted(teamId, checkMode)
    const isSpotting = isTeamSpotting(teamId)
    
    if (isSpotting) {
      return {
        isSpotted: false,
        isSpotting: true,
        canSpot: false,
        buttonText: 'Spotting...',
        buttonVariant: 'default',
        showBadge: false
      }
    }
    
    if (isSpotted) {
      return {
        isSpotted: true,
        isSpotting: false,
        canSpot: false,
        buttonText: 'Spotted',
        buttonVariant: 'secondary',
        showBadge: true
      }
    }
    
    return {
      isSpotted: false,
      isSpotting: false,
      canSpot: true,
      buttonText: 'Spot',
      buttonVariant: 'default',
      showBadge: false
    }
  }

  const spotTeam = async (teamId: string, teamData?: any, location?: string, notes?: string, mode?: TrackingMode) => {
    if (!user?.id) {
      const errorMsg = "Please sign in to spot teams"
      setError(errorMsg)
      throw new Error(errorMsg)
    }

    const spotMode = mode || trackingMode
    
    // Check if already spotted in this mode
    if (isTeamSpotted(teamId, spotMode)) {
      console.log('Team already spotted in this mode:', teamData?.name || teamId, 'mode:', spotMode)
      return true
    }

    // Immediately update spotting state for UI feedback
    setSpottingState(prev => ({
      ...prev,
      spottingTeams: new Set(prev.spottingTeams).add(teamId),
      lastSpottedTeam: teamId
    }))

    try {
      console.log('Spotting team in Firestore:', { teamId, teamName: teamData?.name, location, notes, mode: spotMode })
      
      // Use Firestore to save the spot
      await firestoreSpotTeam(user.id, teamId, location, notes, spotMode)
      
      console.log('Successfully spotted team in Firestore:', teamData?.name || teamId)
      
      // Immediately add to local state for responsive UI
      if (teamData) {
        const spottedTeam = {
          ...teamData,
          spottedAt: new Date(),
          mode: spotMode
        }
        
        setSpottedTeams(prev => {
          // Check if already spotted in the same mode
          if (prev.some(team => team.id === teamId && (team.mode || 'all-time') === spotMode)) {
            console.log('Team already spotted locally in this mode:', spottedTeam.name, 'mode:', spotMode)
            return prev
          }
          console.log('Adding spotted team to local state:', spottedTeam.name, 'mode:', spotMode)
          return [...prev, spottedTeam]
        })

        // Update cache
        setSpottingState(prev => {
          const newCache = new Map(prev.spottedTeamsCache)
          newCache.set(`${teamId}-${spotMode}`, spottedTeam)
          return {
            ...prev,
            spottedTeamsCache: newCache,
            spottingTeams: new Set([...prev.spottingTeams].filter(id => id !== teamId))
          }
        })
      }
      
      return true
    } catch (err) {
      console.error("Error spotting team:", err)
      
      // Categorize error types for better user feedback
      let errorMessage = "Failed to spot team. Please try again."
      
      if (err instanceof Error) {
        if (err.message.includes('permission-denied') || err.message.includes('unauthenticated')) {
          errorMessage = "Authentication error. Please sign in again."
        } else if (err.message.includes('network') || err.message.includes('offline')) {
          errorMessage = "Network error. Check your connection and try again."
        } else if (err.message.includes('quota-exceeded')) {
          errorMessage = "Service temporarily unavailable. Please try again later."
        } else if (err.message.includes('already exists') || err.message.includes('duplicate')) {
          errorMessage = "Team already spotted in this mode."
        }
      }
      
      setError(errorMessage)
      
      // Remove from spotting state on error
      setSpottingState(prev => ({
        ...prev,
        spottingTeams: new Set([...prev.spottingTeams].filter(id => id !== teamId))
      }))
      
      throw new Error(errorMessage)
    }
  }

  // Enhanced spotTeam function with better feedback and retry mechanism
  const spotTeamWithFeedback = async (
    teamId: string, 
    teamData: Team, 
    location?: string, 
    notes?: string, 
    mode?: TrackingMode,
    retryCount: number = 0
  ): Promise<boolean> => {
    const maxRetries = 3
    const retryDelay = 1000 * Math.pow(2, retryCount) // Exponential backoff
    
    try {
      return await spotTeam(teamId, teamData, location, notes, mode)
    } catch (error) {
      console.error(`Error spotting team (attempt ${retryCount + 1}):`, error)
      
      // If we haven't exceeded max retries and it's a network error, retry
      if (retryCount < maxRetries && (
        error instanceof Error && (
          error.message.includes('network') ||
          error.message.includes('timeout') ||
          error.message.includes('fetch')
        )
      )) {
        console.log(`Retrying in ${retryDelay}ms...`)
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return spotTeamWithFeedback(teamId, teamData, location, notes, mode, retryCount + 1)
      }
      
      // If it's not a retryable error or we've exceeded retries, fail
      return false
    }
  }
  
  // Function to remove a specific team spot
  const removeSpot = async (teamId: string, mode?: TrackingMode) => {
    if (!user?.id) {
      setError("User not authenticated")
      return false
    }
    
    try {
      const spotMode = mode || trackingMode
      
      await removeTeamSpot(user.id, teamId, spotMode)
      
      // Remove from local state immediately for responsive UI
      setSpottedTeams(prev => prev.filter(team => team.id !== teamId))
      
      return true
    } catch (err) {
      console.error('Error removing team spot:', err)
      setError(err instanceof Error ? err.message : "An error occurred")
      return false
    }
  }

  // Function to reset trip mode (clear all trip mode spots)
  const resetTripMode = async () => {
    if (!user?.id) {
      setError("User not authenticated")
      return false
    }
    
    try {
      console.log('Resetting trip mode spots for user:', user.id)
      await resetTripModeSpots(user.id)
      
      // If currently in trip mode, refresh to show the reset
      if (trackingMode === 'trip-mode') {
        await fetchSpottedTeams('trip-mode')
      }
      return true
    } catch (err) {
      console.error('Error resetting trip mode:', err)
      setError(err instanceof Error ? err.message : "An error occurred")
      return false
    }
  }
  
  // Switch between tracking modes with state update
  const switchTrackingMode = (mode: TrackingMode) => {
    setTrackingMode(mode)
    fetchSpottedTeams(mode)
  }

  const switchModeWithStateUpdate = async (mode: TrackingMode): Promise<void> => {
    setTrackingMode(mode)
    await fetchSpottedTeams(mode)
  }

  // Refresh spotted teams with caching optimization
  const refreshSpottedTeams = async (): Promise<void> => {
    // Clear cache before refreshing
    setSpottingState(prev => ({
      ...prev,
      spottedTeamsCache: new Map()
    }))
    await fetchSpottedTeams(trackingMode)
  }

  // Get spotted teams for a specific mode with caching
  const getSpottedTeamsForMode = (mode: TrackingMode) => {
    // Use cache if available for better performance
    const cachedTeams = Array.from(spottingState.spottedTeamsCache.values())
      .filter(team => (team.mode || 'all-time') === mode)
    
    if (cachedTeams.length > 0) {
      return cachedTeams
    }
    
    return spottedTeams.filter(team => (team.mode || 'all-time') === mode)
  }

  // Fetch teams when user changes
  useEffect(() => {
    if (user) {
      fetchSpottedTeams('all-time')
    } else {
      setSpottedTeams([])
      setLoading(false)
    }
  }, [user])

  // Debug: Log when spotted teams change
  useEffect(() => {
    console.log('Spotted teams updated:', spottedTeams.map(t => ({ id: t.id, name: t.name })))
  }, [spottedTeams])

  return {
    // Existing functionality
    spottedTeams,
    loading,
    error,
    trackingMode,
    spotTeam,
    removeSpot,
    resetTripMode,
    switchTrackingMode,
    refetch: fetchSpottedTeams,
    
    // Enhanced functionality
    spottingState,
    isTeamSpotted,
    isTeamSpotting,
    getSpotButtonState,
    spotTeamWithFeedback,
    refreshSpottedTeams,
    switchModeWithStateUpdate,
    getSpottedTeamsForMode,
  }
}
